/**
 * StripeModeBanner — owner-only diagnostic banner.
 *
 * Checks /api/owner/diagnostics/stripe-mode on mount. If the backend reports
 * anything other than `live` mode (test / emergent_test / not_configured),
 * it shows a bright red warning bar at the top of the Coach console — so
 * a misconfigured production deploy is impossible to miss.
 *
 * When mode === "live" the component renders nothing. Silent unless wrong.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_COPY = {
    test: {
        title: "STRIPE IS IN TEST MODE",
        body: "Real cards will be declined. Update STRIPE_API_KEY in your deployment secrets to a sk_live_… key, then redeploy.",
    },
    emergent_test: {
        title: "STRIPE IS USING THE EMERGENT TEST KEY",
        body: "Real cards will be declined. Set STRIPE_API_KEY in your deployment secrets to your own sk_live_… key, then redeploy.",
    },
    not_configured: {
        title: "STRIPE IS NOT CONFIGURED",
        body: "STRIPE_API_KEY is missing on the server. Payments are completely disabled until you set it in deployment secrets and redeploy.",
    },
    unknown: {
        title: "STRIPE KEY FORMAT NOT RECOGNIZED",
        body: "The configured STRIPE_API_KEY doesn't look like a sk_live_… or sk_test_… key. Double-check the value in deployment secrets.",
    },
};

export default function StripeModeBanner({ role }) {
    const [mode, setMode] = useState(null);
    const [webhookOk, setWebhookOk] = useState(true);
    const [dismissedAt, setDismissedAt] = useState(
        () => Number(localStorage.getItem("stripe_mode_banner_dismissed_at") || 0)
    );

    useEffect(() => {
        if (role !== "owner") return;
        axios
            .get(`${API}/owner/diagnostics/stripe-mode`, { withCredentials: true })
            .then((r) => {
                setMode(r.data?.mode || "unknown");
                setWebhookOk(Boolean(r.data?.webhook_configured));
            })
            .catch(() => {
                // 401/403/network — quietly skip; we only nag the owner.
            });
    }, [role]);

    if (role !== "owner" || !mode || mode === "live") return null;

    // Re-show the banner if dismissed more than 24h ago.
    const dismissedRecently = dismissedAt && (Date.now() - dismissedAt) < 24 * 60 * 60 * 1000;
    if (dismissedRecently) return null;

    const copy = MODE_COPY[mode] || MODE_COPY.unknown;
    const extra = !webhookOk && mode === "live"
        ? " Webhook secret also missing — subscription events will not sync."
        : "";

    const dismiss = () => {
        const now = Date.now();
        localStorage.setItem("stripe_mode_banner_dismissed_at", String(now));
        setDismissedAt(now);
    };

    return (
        <div
            data-testid="stripe-mode-banner"
            className="bg-brand-red text-white px-4 py-3 border-b border-brand-red/40"
        >
            <div className="max-w-7xl mx-auto flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                    <div className="font-display tracking-wider text-sm sm:text-base font-bold">
                        {copy.title}
                    </div>
                    <p className="text-xs sm:text-sm mt-1 leading-snug">
                        {copy.body}{extra} Once fixed, run{" "}
                        <code className="bg-black/30 px-1 py-0.5 rounded text-[11px]">
                            /api/owner/diagnostics/stripe-mode
                        </code>{" "}
                        — mode should read <strong>live</strong>.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={dismiss}
                    data-testid="stripe-mode-banner-dismiss"
                    className="text-[10px] tracking-[0.2em] uppercase border border-white/60 px-2 py-1 hover:bg-white hover:text-brand-red transition-colors flex-shrink-0"
                    title="Hide for 24h"
                >
                    Hide 24h
                </button>
            </div>
        </div>
    );
}
